import React, { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import { ChatMessage } from '../types/askStanley.types';
import { createIconElement } from '@/lib/utils/createIconElement';
import { useAskStanley } from '../hooks/useAskStanley';
import IcThumbsUp from '@/assets/icons/ic-thumbs-up.svg';
import IcThumbsDown from '@/assets/icons/ic-thumbs-down.svg';
import IcRefresh from '@/assets/icons/ic-refresh-02.svg';
import IcCopy from '@/assets/icons/ic-copy.svg';
import IcCheck from '@/assets/icons/ic-check.svg';

interface MessageActionsProps {
    message: ChatMessage;
}

export const MessageActions = ({ message }: MessageActionsProps) => {
    const { giveFeedback, copyMessage, refreshMessage, isLoading, isStreaming, chats, currentChatId } = useAskStanley();
    const [copiedMessageId, setCopiedMessageId] = useState<string | number | null>(null);

    const currentChat = chats.find(chat => chat.id === currentChatId);
    const messages = currentChat?.messages || [];

    // Copy message handler
    const handleCopy = async () => {
        await copyMessage(message.id);
        // Show checkmark for 2 seconds
        setCopiedMessageId(message.id);
        setTimeout(() => {
            setCopiedMessageId(null);
        }, 2000);
    };

    // Refresh message handler
    const handleRefresh = () => {
        // Find the previous user message to re-send
        const messageIndex = messages.findIndex(msg => msg.id === message.id);
        const previousMessage = messages[messageIndex - 1];
        if (previousMessage && previousMessage.type === 'sent') {
            refreshMessage(message.id, previousMessage.content, previousMessage.attachments);
        }
    };

    const handleThumbsUp = () => giveFeedback(message.id, 1);
    const handleThumbsDown = () => giveFeedback(message.id, -1);

    return (
        <div className='mt-2 ml-2 flex items-center gap-2'>
            {/* Thumbs Up Button - only show if no feedback given or thumbs up was clicked */}
            {(message.feedback_status === null ||
                message.feedback_status === undefined ||
                message.feedback_status === 1) && (
                <button
                    onClick={handleThumbsUp}
                    className={cn(
                        'cursor-pointer rounded p-1 transition-colors',
                        message.feedback_status === 1 ? 'text-success' : 'hover:text-success text-gray'
                    )}
                    disabled={isLoading || isStreaming}
                    aria-label='Thumbs up'
                >
                    {createIconElement(IcThumbsUp, { className: 'size-5' })}
                </button>
            )}

            {/* Thumbs Down Button - only show if no feedback given or thumbs down was clicked */}
            {(message.feedback_status === null ||
                message.feedback_status === undefined ||
                message.feedback_status === -1) && (
                <button
                    onClick={handleThumbsDown}
                    className={cn(
                        'cursor-pointer rounded p-1 transition-colors',
                        message.feedback_status === -1 ? 'text-error' : 'hover:text-error text-gray'
                    )}
                    disabled={isLoading || isStreaming}
                    aria-label='Thumbs down'
                >
                    {createIconElement(IcThumbsDown, { className: 'size-5' })}
                </button>
            )}

            <button
                onClick={handleRefresh}
                className='text-gray hover:text-gray cursor-pointer rounded p-1 transition-colors'
                disabled={isLoading || isStreaming}
                aria-label='Refresh'
            >
                {createIconElement(IcRefresh, { className: 'size-5' })}
            </button>

            <button
                onClick={handleCopy}
                className={cn(
                    'cursor-pointer rounded p-1 transition-colors',
                    copiedMessageId === message.id ? 'text-success' : 'text-gray hover:text-gray'
                )}
                aria-label='Copy message'
            >
                {copiedMessageId === message.id
                    ? createIconElement(IcCheck, { className: 'size-5' })
                    : createIconElement(IcCopy, { className: 'size-5' })}
            </button>
        </div>
    );
};
